import { parseJoinResponse } from './join';
import type { JoinCredentials } from './join';

/**
 * Webinar speak-grant REST surface (control/internal/httpapi/speak.go, ADR 0010): a
 * moderator/admin grants (POST) or revokes (DELETE) an identity's right to speak, and the
 * granted viewer mints its own publishing token from POST speak-token with its current join
 * JWT as the bearer. Same-origin /api calls, exactly like join/remint/recording.
 */

/** Failure of a speak endpoint: HTTP error, network failure or malformed response body. */
export class SpeakApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'SpeakApiError';
    this.status = status;
    this.code = code;
  }
}

/** Body of a successful grant/revoke (speak.go speakResponse). */
interface SpeakResponse {
  readonly identity: string;
  readonly granted: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Builds the SpeakApiError for a non-2xx response from the contract error envelope. */
async function errorFromResponse(response: Response, endpoint: string): Promise<SpeakApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // non-JSON error body, keep the generic message
  }
  let code = 'unknown_error';
  let message = `${endpoint} request failed with HTTP ${response.status}`;
  if (isRecord(body) && isRecord(body.error)) {
    if (typeof body.error.code === 'string') {
      code = body.error.code;
    }
    if (typeof body.error.message === 'string') {
      message = body.error.message;
    }
  }
  return new SpeakApiError(response.status, code, message);
}

/**
 * Shared front of the grant/revoke routes: both answer the speakResponse shape, both are
 * moderator/admin gated server-side (participants get 403 forbidden).
 */
async function speakTargetRequest(
  room: string,
  identity: string,
  method: 'POST' | 'DELETE',
  token: string,
): Promise<SpeakResponse> {
  let response: Response;
  try {
    response = await fetch(
      `/api/v1/rooms/${encodeURIComponent(room)}/speak/${encodeURIComponent(identity)}`,
      { method, headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    throw new SpeakApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, `speak/${identity}`);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new SpeakApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  if (
    !isRecord(body) ||
    typeof body.identity !== 'string' ||
    body.identity.length === 0 ||
    typeof body.granted !== 'boolean'
  ) {
    throw new SpeakApiError(
      response.status,
      'invalid_response',
      'speak response violates the speak shape',
    );
  }
  return { identity: body.identity, granted: body.granted };
}

/** POST /api/v1/rooms/{room}/speak/{identity} (moderator/admin): grant the right to speak. */
export function grantSpeak(room: string, identity: string, token: string): Promise<SpeakResponse> {
  return speakTargetRequest(room, identity, 'POST', token);
}

/**
 * DELETE /api/v1/rooms/{room}/speak/{identity} (moderator/admin): revoke the grant. Revoking
 * an identity without one is an idempotent success; admins cannot be denied (403).
 */
export function revokeSpeak(room: string, identity: string, token: string): Promise<SpeakResponse> {
  return speakTargetRequest(room, identity, 'DELETE', token);
}

/**
 * POST /api/v1/rooms/{room}/speak-token: the granted viewer's own call. The bearer is the
 * caller's current join JWT; the answer is the join-shaped response whose token carries the
 * publish grants (parseJoinResponse re-parses it: token/url/expiresAt/identity/role/turn).
 * Enforcement is server-side: no grant (403), drained remint bucket (429), kick tombstone.
 */
export async function fetchSpeakToken(room: string, token: string): Promise<JoinCredentials> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/rooms/${encodeURIComponent(room)}/speak-token`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new SpeakApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'speak-token');
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new SpeakApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  try {
    return parseJoinResponse(body);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed speak-token response';
    throw new SpeakApiError(response.status, 'invalid_response', detail);
  }
}
